import React from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  VStack,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useDisclosure,
} from '@chakra-ui/react';
import {IntegrationDetailsHeader} from './IntegrationDetailsHeader';
import {useIntegrationFromContext} from './IntegrationDetailsContext';
import {IntegrationSection} from './components';
import {DeleteIntegrationButton} from './DeleteIntegrationButton';
import {CodeSnippet} from '../CodeSnippet';
import {useCustomer} from '../../hooks/api/metadata';
import {ObjectId} from 'bson';
import {VerifyWebIntentInstallationModal} from './VerifyWebIntentInstallationModal';
import {WebIntentSourceModal} from './WebIntentSourceModal';
import {DeleteWebIntentSourceModal} from './DeleteWebIntentSourceModal';
import {MaxIntentSources} from '../../shared/api/definitions/intent';
import {Edit, TrashCan} from '@carbon/icons-react';
import {WebIntentSource} from '../../shared/intent';

export const getEmbedScript = ({
  profileId,
  customerId,
}: {
  profileId: string;
  customerId: ObjectId;
}) => `<script>
  !(function (e) {
    'use strict';
    var a = e && e.namespace;
    if (a && e.profileId && e.cdn) {
      var r = window[a];
      if (
        ((r && Array.isArray(r)) || (r = window[a] = []),
        !r.initialized && !r._loaded)
      )
        if (r._loaded)
          console &&
            console.warn(
              '[Keyplay Intent] Duplicate initialization attempted'
            );
        else {
          r._loaded = !0;
          [
            'track',
            'page',
            'identify',
            'group',
            'alias',
            'ready',
            'debug',
            'on',
            'off',
            'once',
            'trackClick',
            'trackSubmit',
            'trackLink',
            'trackForm',
            'pageview',
            'screen',
            'reset',
            'register',
            'setAnonymousId',
            'addSourceMiddleware',
            'addIntegrationMiddleware',
            'addDestinationMiddleware',
          ].forEach(function (e) {
            var i;
            r[e] =
              ((i = e),
              function () {
                var e = window[a];
                if (e.initialized) return e[i].apply(e, arguments);
                var r = [].slice.call(arguments);
                return r.unshift(i), e.push(r), e;
              });
          }),
            (r.bootstrap = function () {
              var a = document.createElement('script');
              (a.async = !0),
                (a.type = 'text/javascript'),
                (a.id = '__radar__'),
                (a.dataset.settings = JSON.stringify(e)),
                (a.src =
                  'https://' + e.cdn + '/releases/latest/radar.min.js?kp_cid=${encodeURIComponent(
                    customerId.toString()
                  )}');
              var r = document.scripts[0];
              r.parentNode.insertBefore(a, r);
            }),
            r.bootstrap();
        }
    } else
      'undefined' != typeof console &&
        console.error('[Keyplay Intent] Configuration incomplete');
  })({
    cdn: 'cdn.keyplay.io',
    apiEndpoint: 'intent.keyplay.io',
    profileId: '${profileId}',
    namespace: 'Keyplay',
  });
</script>`;

const WebIntentSourceRow = ({source}: {source: WebIntentSource}) => {
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();

  return (
    <>
      <Tr>
        <Td>{source.label}</Td>
        <Td fontFamily="mono" fontSize="sm">
          {source.path}
        </Td>
        <Td>
          <Flex gap={1}>
            <IconButton
              aria-label="Edit source"
              icon={<Edit />}
              size="sm"
              variant="ghost"
              onClick={editModal.onOpen}
            />
            <IconButton
              aria-label="Delete source"
              colorScheme="red"
              icon={<TrashCan />}
              size="sm"
              variant="ghost"
              onClick={deleteModal.onOpen}
            />
          </Flex>
        </Td>
      </Tr>
      <WebIntentSourceModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        intentSource={source}
      />
      <DeleteWebIntentSourceModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        intentSource={source}
      />
    </>
  );
};

export const WebIntentIntegrationDetails = () => {
  const integration = useIntegrationFromContext('webIntent');
  const customer = useCustomer();
  const allIntentSources = customer.intentSources ?? [];
  const sources = allIntentSources.filter(
    (source) => source.type === 'webIntent'
  );

  const verifyModal = useDisclosure();
  const createModal = useDisclosure();

  return (
    <Flex direction="column">
      <IntegrationDetailsHeader>
        <DeleteIntegrationButton />
      </IntegrationDetailsHeader>

      <VerifyWebIntentInstallationModal
        isOpen={verifyModal.isOpen}
        onClose={verifyModal.onClose}
      />

      <WebIntentSourceModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
      />

      <Flex direction="column" my={8} gap={4}>
        <IntegrationSection>
          <VStack align="start" spacing="4">
            <Box fontSize="lg" fontWeight="500">
              Installation
            </Box>
            <Text>
              Add the tracking script to your website to start capturing visitor
              intent. Place it in the <Code fontSize="sm">&lt;head&gt;</Code>{' '}
              section of your HTML.
            </Text>
            <Box w="100%">
              <CodeSnippet
                code={getEmbedScript({
                  profileId: integration.trackingScriptId,
                  customerId: customer._id,
                })}
              />
            </Box>
            <Flex align="center" gap={3}>
              <Button
                colorScheme="kbuttonblue"
                isDisabled={!!integration.trackingScriptDetectedAt}
                onClick={verifyModal.onOpen}
              >
                Verify Installation
              </Button>
              {integration.trackingScriptDetectedAt && (
                <Text fontSize="sm" color="kgreen.400">
                  Verified on{' '}
                  {integration.trackingScriptDetectedAt.toLocaleDateString()}
                </Text>
              )}
            </Flex>
          </VStack>
        </IntegrationSection>

        <IntegrationSection>
          <VStack align="start" spacing="4">
            <Flex w="100%" justify="space-between" align="center">
              <Box fontSize="lg" fontWeight="500">
                Sources
              </Box>
              <Button
                size="sm"
                colorScheme="kbuttonblue"
                onClick={createModal.onOpen}
                isDisabled={allIntentSources.length >= MaxIntentSources}
              >
                Create
              </Button>
            </Flex>
            <Text fontSize="sm" color="kgray.300">
              Configured web pages for intent data collection.
            </Text>
            {sources.length === 0 ? (
              <Text fontSize="sm" color="kgray.300">
                No sources configured yet. Create one to get started.
              </Text>
            ) : (
              <TableContainer w="100%">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Label</Th>
                      <Th>Path</Th>
                      <Th w="1%" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sources.map((source) => (
                      <WebIntentSourceRow
                        key={source.id.toString()}
                        source={source}
                      />
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </VStack>
        </IntegrationSection>
      </Flex>
    </Flex>
  );
};
